import { VStack } from '@sgme/ui';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { NegociationCard } from '@/App/VoiceTimestamping/NegociationCard';
import { useTrackEvent } from '@/App/VoiceTimestamping/useTrackEvent.ts';
import { pretradeApi } from '@/store/pretrade/api/index.ts';
import type { Negociation, NegociationId } from '@/store/pretrade/api/types.ts';

const ENDED_NEGOCIATION_DISPLAY_DELAY = 10000;

export const NegociationList = () => {
  const trackEvent = useTrackEvent();
  const [createNegociation] = pretradeApi.useCreateNegociationMutation();
  const [negociations, setNegociations] = useState<Negociation[]>([]);
  const [addNegociationInProgress, setAddNegociationInProgress] = useState(false);
  const [isError, setIsError] = useState(false);

  const addNegociation = useCallback(async () => {
    const createdAt = Date.now();
    setAddNegociationInProgress(true);

    trackEvent('Start negociation');
    const { data: negociationId, error } = await createNegociation(createdAt);
    setAddNegociationInProgress(false);

    setIsError(!!error);

    if (negociationId !== undefined) {
      setNegociations((negociations) => [
        ...negociations,
        {
          id: negociationId,
          createdAt,
          quotes: [],
          status: 'in progress',
        },
      ]);
    }
  }, [createNegociation]);

  const handleNegociationEnded = useCallback(
    (id: NegociationId, dealDone: boolean) => {
      const negociation = negociations.find((negociation) => negociation.id === id);
      if (!negociation) {
        console.error(`Negociation with id ${id} not found`);
        return;
      }

      negociation.status = dealDone ? 'done' : 'missed';
      setTimeout(() => {
        setNegociations((negociations) => negociations.filter((negociation) => negociation.id !== id));
      }, ENDED_NEGOCIATION_DISPLAY_DELAY);
    },
    [negociations.find, negociations],
  );

  return (
    <VStack className="pt-3" gap="12px">
      {isError && (
        <div role="alert" className="alert alert-outline-danger text-center">
          <FormattedMessage id="voiceTimestamping.panel.error" />
        </div>
      )}
      {negociations.map((negociation) => (
        <NegociationCard
          key={negociation.id}
          negociation={negociation}
          onHasError={setIsError}
          onNegociationEnded={handleNegociationEnded}
        />
      ))}
      <button
        type="button"
        className="btn btn-icon-text btn-primary w-100"
        onClick={addNegociation}
        disabled={addNegociationInProgress}
      >
        <i className="icon">add</i>
        <FormattedMessage id="voiceTimestamping.panel.addNegociation" />
      </button>
    </VStack>
  );
};
